import { useHistory } from 'react-router-dom';
import {
  Heading,
  Flex,
  Button,
  Icon,
  Text,
  useToast,
  ButtonGroup,
  HStack,
  VStack,
  Divider,
  useBreakpointValue,
  FlexProps,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  RiBookmarkLine,
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleLine,
  RiDeleteBinLine,
  RiEditLine,
} from 'react-icons/ri';
import { useCallback, useState } from 'react';
import axios from 'axios';
import { ConfirmationModal } from '../../../../../../../../components/ConfirmationModal';
import { LinkButton } from '../../../../../../../../components/LinkButton';
import { useAuth } from '../../../../../../../../hooks/auth';
import { useProduct } from '../../../../../../../../hooks/product';
import { translateError } from '../../../../../../../../utils/errors';
import deleteProductsService from '../../../../../../../../services/Products/DeleteProductsService';
import { ProductInvoiceType } from '../../../../../../../../models/products';

export const ProductDetailsInfo = (props: FlexProps): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment } = useAuth();

  const { product } = useProduct();

  const { push } = useHistory();

  const dividerOrientation = useBreakpointValue({
    base: 'horizontal',
    xl: 'vertical',
  });

  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);

  const handleToggleDeleteConfirmationModal = useCallback(() => {
    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleDeleteProduct = useCallback(async () => {
    if (product?.id) {
      try {
        await deleteProductsService(product.id);

        toast({
          title: 'Excluído com sucesso',
          description: 'O produto foi excluído corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        push('/products');
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          handleToggleDeleteConfirmationModal();

          toast({
            title: 'Falha ao excluir',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao excluir o produto, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }
  }, [product?.id, handleToggleDeleteConfirmationModal, push, toast]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteConfirmationModalVisible}
        onClose={handleToggleDeleteConfirmationModal}
        onConfirm={handleDeleteProduct}
        title="Confirmar exclusão"
        message="Deseja realmente excluir?"
      />

      <Flex
        direction="column"
        w="100%"
        borderRadius={8}
        bg="white"
        p="8"
        {...props}
      >
        <Flex justify="space-between" align="center">
          <Heading size="lg" fontWeight="normal">
            Detalhes do produto
          </Heading>
        </Flex>

        {product && (
          <Flex direction="column" mt="8">
            <Grid
              templateColumns={[
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(12, 1fr)',
              ]}
              gap="4"
              width="100%"
            >
              <GridItem colSpan={[12, 12, 12, 12, 8]}>
                <Flex direction="column" px="8">
                  <Flex alignItems="flex-start">
                    <VStack flex="1" alignItems="flex-start" ml="8">
                      <Text fontSize="4xl" fontWeight="bold">
                        {product.name}
                      </Text>

                      <HStack>
                        <Text>Centro de custo:</Text>
                        <Text color="gray.500">{product.costCenter}</Text>
                      </HStack>

                      <HStack>
                        <Text>Código de produto:</Text>
                        <Text color="gray.500">{product.productCode}</Text>
                      </HStack>

                      <HStack>
                        <Text>Tipo de produto:</Text>
                        <Text color="gray.500">{product.productType}</Text>
                      </HStack>

                      <HStack>
                        <Text>Natureza de produto:</Text>
                        <Text color="gray.500">{product.productNature}</Text>
                      </HStack>

                      <HStack>
                        <Text>Tipo de nota:</Text>
                        <Text color="gray.500">
                          {ProductInvoiceType[product.invoiceType]}
                        </Text>
                      </HStack>

                      <HStack>
                        <Text>Ativo:</Text>
                        {product.isActive ? (
                          <Icon
                            fontSize="3xl"
                            as={RiCheckboxCircleLine}
                            color="green"
                          />
                        ) : (
                          <Icon
                            fontSize="3xl"
                            as={RiCheckboxBlankCircleLine}
                            color="gray.400"
                          />
                        )}
                      </HStack>
                    </VStack>

                    <Divider
                      hidden={dividerOrientation === 'horizontal'}
                      ml="4"
                      alignSelf="center"
                      height="80%"
                      borderColor="gray.300"
                      orientation="vertical"
                    />
                  </Flex>

                  <ButtonGroup mt="8" ml="8">
                    {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        [
                          'PRODUCT_FULL_ACCESS',
                          'PRODUCT_WRITE_ACCESS',
                        ].includes(feature.key),
                    ) && (
                      <LinkButton
                        colorScheme="yellow"
                        color="white"
                        icon={<Icon as={RiEditLine} fontSize="16" />}
                        to={{
                          pathname: '/services/products/update',
                          state: {
                            productId: product.id,
                          },
                        }}
                      >
                        Editar
                      </LinkButton>
                    )}

                    {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        [
                          'PRODUCT_FULL_ACCESS',
                          'PRODUCT_DELETE_ACCESS',
                        ].includes(feature.key),
                    ) && (
                      <Button
                        size="sm"
                        fontSize="sm"
                        colorScheme="red"
                        onClick={handleToggleDeleteConfirmationModal}
                        leftIcon={<Icon as={RiDeleteBinLine} fontSize="20" />}
                      >
                        Excluir
                      </Button>
                    )}
                  </ButtonGroup>
                </Flex>
              </GridItem>

              {product.description && (
                <GridItem colSpan={[12, 12, 12, 12, 4]}>
                  <Flex height="100%">
                    <Divider
                      hidden={dividerOrientation === 'horizontal'}
                      px="2"
                      height="80%"
                      borderColor="gray.300"
                      orientation="vertical"
                    />

                    <Flex flexDirection="column" w="100%">
                      <Divider
                        hidden={dividerOrientation === 'vertical'}
                        borderColor="gray.300"
                        mt="4"
                        mb="8"
                      />
                      <Flex
                        flexDirection="column"
                        px={[8, 8, 8, 8, 0]}
                        w="100%"
                      >
                        <HStack mt={[0, 0, 0, 0, 2]}>
                          <Icon as={RiBookmarkLine} fontSize="20" />
                          <Text>Descrição</Text>
                        </HStack>

                        <Text
                          fontSize="sm"
                          color="gray.500"
                          mt="4"
                          px={[4, 4, 4, 4, 0]}
                        >
                          {product.description}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </GridItem>
              )}
            </Grid>
          </Flex>
        )}
      </Flex>
    </>
  );
};
